import classNames from "classnames";
import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent
} from "react";
import { FormSelectItem } from "../../../../Blocks/ProvetOnlineBooking/types/formSelectItem";
import { isNullOrWhiteSpace } from "../../../../Shared/Utilities/string";
import CountryCodeSelect from "./countryCodeSelect";
import { CountryCodeSelectItems } from "./countryCodeSelectItems";
import {
  getCountryCodeByLanguage,
  joinPhoneNumber,
  splitPhoneNumber
} from "./phoneNumberInputUtilities";

interface PhoneNumberInputProps {
  id?: string;
  language?: string;
  disabled?: boolean;
  name?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  countryCodeAriaLabel?: string;
  required?: boolean;
  value?: string;
  invalid?: boolean;
  onValueChanged?: (value: string) => void;
  onBlur?: () => void;
  additionalClass?: string;
}

export const PhoneNumberInput = memo(function PhoneNumberInput({
  id,
  language,
  disabled = false,
  name,
  placeholder,
  searchPlaceholder,
  countryCodeAriaLabel,
  required = false,
  value,
  invalid,
  onValueChanged,
  onBlur,
  additionalClass
}: PhoneNumberInputProps) {
  const [countryCodeItem, setCountryCodeItem] = useState<FormSelectItem>(
    getCountryCodeByLanguage(language)
  );
  const [phone, setPhone] = useState<string>("");
  const hasUserTypedRef = useRef(false);
  const hasUserInteractedRef = useRef(false);
  const onValueChangedRef = useRef(onValueChanged);

  // Keep the callback ref up to date
  useEffect(() => {
    onValueChangedRef.current = onValueChanged;
  }, [onValueChanged]);

  // Split incoming value prop into countryCode and phone
  useEffect(() => {
    if (!isNullOrWhiteSpace(value) && !hasUserTypedRef.current) {
      const { countryCode: cc, localNumber } = splitPhoneNumber(value);
      const item =
        CountryCodeSelectItems.find(i => i.value === cc) || CountryCodeSelectItems[0];
      setCountryCodeItem(item);
      setPhone(localNumber);
    }
  }, [value]);

  // Combine country code and phone and notify parent when they change
  useEffect(() => {
    // Skip if country code isn't set yet
    if (!countryCodeItem || !countryCodeItem.value) {
      return;
    }

    // Only call onValueChanged if user has typed something
    if (hasUserTypedRef.current && onValueChangedRef.current) {
      const fullValue = joinPhoneNumber(countryCodeItem.value, phone);
      onValueChangedRef.current(fullValue);
    }
  }, [countryCodeItem, phone]);

  const phoneNumberInputRef = useRef<HTMLInputElement>(null);

  const handleInteraction = useCallback(() => {
    // Mark that user has interacted with this field
    hasUserInteractedRef.current = true;
  }, []);

  const handleFocus = useCallback((e: FocusEvent<HTMLInputElement>) => {
    // Check if relatedTarget exists and is within the same form/container
    if (e.relatedTarget !== null) {
      // Find the closest form or contact details container
      const container = phoneNumberInputRef.current?.closest("#provet-contact-details");
      const isRelatedTargetInSameContainer = container?.contains(e.relatedTarget as Node);

      // Only mark as interacted if focus came from within the same form
      // (user tabbing between fields), not from outside (step navigation button)
      if (isRelatedTargetInSameContainer) {
        hasUserInteractedRef.current = true;
      }
    }
  }, []);

  const handleBlur = useCallback(() => {
    // Only call onBlur if user has actually interacted with the field
    if (hasUserInteractedRef.current && onBlur) {
      onBlur();
    }
  }, [onBlur]);

  const handlePhoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    // Mark as typed when user types
    if (!hasUserTypedRef.current) {
      hasUserTypedRef.current = true;
    }
    // User is definitely interacting
    hasUserInteractedRef.current = true;
    setPhone(e.target.value);
  }, []);

  const handleCountryCodeChange = useCallback(
    (item: FormSelectItem, isUserAction: boolean = false) => {
      // Mark as typed if this is a user action (not initial mount)
      if (isUserAction && !hasUserTypedRef.current) {
        hasUserTypedRef.current = true;
      }
      setCountryCodeItem(item);
    },
    []
  );

  return (
    <div className={classNames("flex flex-col gap-xs", additionalClass)}>
      <label className="flex flex-col gap-xs">
        <span
          className={classNames(
            "flex p-xs rounded-tl-br-md bg-neutral-0 border border-neutral-300 h-3xl transition-short-in-out",
            "hover:border-dark-blue",
            "focus-outline has-[:focus-visible]:outline",
            {
              "input-validation-error !border-error-200 has-[:focus-visible]:outline-error-200/75":
                invalid
            }
          )}
        >
          <span className="w-full flex gap-xs items-center">
            <CountryCodeSelect
              id="country-code"
              name="country-code"
              language={language}
              searchPlaceholder={searchPlaceholder}
              ariaLabel={countryCodeAriaLabel}
              onChange={handleCountryCodeChange}
              disabled={disabled}
              value={countryCodeItem}
            />
            <input
              id={id}
              type="tel"
              name={name}
              disabled={disabled}
              required={required}
              placeholder={placeholder}
              value={phone}
              onChange={handlePhoneChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onMouseDown={handleInteraction}
              ref={phoneNumberInputRef}
              className={classNames(
                "w-full px-md py-sm text-t-sm placeholder-t-neutral-700 text-dark-blue focus:[outline-style:none]",
                "rounded-tl-br-md",
                {
                  "bg-neutral-100/30 border-neutral-400/30 cursor-not-allowed": disabled,
                  "placeholder:text-neutral-400/70 text-neutral-600/70": disabled,
                  "border-error-200 focus:border-error-200 focus:ring-error-200": invalid
                }
              )}
              data-f-datainput="true"
              autoComplete="off"
            />
          </span>
        </span>
      </label>
    </div>
  );
});

export default PhoneNumberInput;
